import { Badge, Banner, Button, Checkbox, Dialog, Field, Input, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { DangerConfirmModal } from '../components/DangerConfirmModal';
import { DangerButton } from '../components/ui/DangerButton';
import { ICONS } from '../components/ui/icons';
import { SkeletonText } from '../components/ui/Skeleton';

interface ApiKeyRow { id: string; name: string; prefix: string; lastUsedAt: string | null; createdAt: string; revokedAt: string | null }
interface Endpoint { id: string; url: string; events: string[]; active: boolean; failureCount: number; lastDeliveryAt: string | null; lastStatus: number | null; lastError: string | null }
interface Delivery { id: string; endpointId: string; event: string; status: string; attempts: number; responseStatus: number | null; error: string | null; createdAt: string }
interface Overview { keys: ApiKeyRow[]; endpoints: Endpoint[]; deliveries: Delivery[]; events: Array<{ id: string; label: string }> }

const DELIVERY: Record<string, { label: string; variant: 'green' | 'blue' | 'red' | 'lightgray' }> = {
  SUCCESS: { label: 'Entregue', variant: 'green' }, PENDING: { label: 'Nova tentativa', variant: 'blue' },
  SENDING: { label: 'Enviando', variant: 'blue' }, FAILED: { label: 'Falhou', variant: 'red' },
};

/** Cores do `Badge` do Cloudscape: `lightgray` → `grey`. */
const BADGE_COLOR: Record<string, 'green' | 'blue' | 'red' | 'neutral'> = {
  green: 'green', blue: 'blue', red: 'red', lightgray: 'neutral',
};

/** Organização › API e webhooks */
export function IntegracoesPage() {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [data, setData] = useState<Overview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [newKeyOpen, setNewKeyOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [hookOpen, setHookOpen] = useState(false);
  const [hook, setHook] = useState<{ url: string; events: string[] }>({ url: '', events: [] });
  const [secret, setSecret] = useState<{ title: string; value: string; hint: string } | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);
  const [removing, setRemoving] = useState<Endpoint | null>(null);
  const canManage = can('integrations:manage');
  const base = `${window.location.origin}/api/v1`;

  const load = useCallback(async () => {
    const res = await fetchApi('/api/integracoes');
    if (res.ok) setData(await res.json());
  }, [fetchApi]);
  useEffect(() => { if (canManage) load(); }, [load, canManage]);

  const call = async (key: string, url: string, init: RequestInit, ok?: string) => {
    setBusy(key);
    try {
      const res = await fetchApi(url, init);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || body.error || 'Não foi possível concluir');
      if (ok) toast.success(ok);
      return body;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível concluir');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copiado'); } catch { /* sem permissão */ }
  };

  if (!canManage) {
    return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="alert" description={<>Só administradores da organização gerenciam a API e os webhooks.</>} /></div>;
  }
  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;

  const label = (id: string) => data.events.find((e) => e.id === id)?.label ?? id;
  const activeKeys = data.keys.filter((k) => !k.revokedAt);

  if (newKeyOpen) {
    return (
      <FormPage
        backTo="/configuracoes"
        backLabel="Integrações"
        onBack={() => setNewKeyOpen(false)}
        title={'Nova chave de API'}
        description="Use a chave para integrar sistemas externos à API."
        submitLabel={'Criar chave'}
        submitting={busy === 'key'}
        onSubmit={async () => {
    const r = await call('key', '/api/integracoes/chaves', { method: 'POST', body: JSON.stringify({ name: keyName }) });
    if (r) { setNewKeyOpen(false); setSecret({ title: 'Chave criada', value: r.key, hint: 'Copie agora: por segurança, ela não aparece de novo.' }); load(); }
  }}
        onCancel={() => setNewKeyOpen(false)}
      >
        <FormSection title="Detalhes">


        <Field label="Nome" description="Para saber onde ela é usada (ex.: n8n, site, ERP)">
          <Input onChange={(e) => setKeyName(e.target.value)} value={keyName} />
        </Field>
        
        </FormSection>
      </FormPage>
    );
  }

  if (hookOpen) {
    return (
      <FormPage
        backTo="/configuracoes"
        backLabel="Integrações"
        onBack={() => setHookOpen(false)}
        title={'Novo webhook'}
        description="Receba eventos da plataforma em uma URL sua."
        submitLabel={'Criar webhook'}
        submitting={busy === 'hook'}
        submitDisabled={!hook.url || !hook.events.length}
        onSubmit={async () => {
    const r = await call('hook', '/api/integracoes/webhooks', { method: 'POST', body: JSON.stringify(hook) });
    if (r) { setHookOpen(false); setSecret({ title: 'Webhook criado', value: r.secret, hint: 'Este é o segredo para conferir a assinatura. Copie agora: ele não aparece de novo.' }); load(); }
  }}
        onCancel={() => setHookOpen(false)}
      >
        <FormSection title="Detalhes">


        <Field label="Endereço (https)">
          <Input onChange={(e) => setHook({ ...hook, url: e.target.value })} placeholder="https://api.suaempresa.com.br/webhooks/ingrave" value={hook.url} />
        </Field>
        <div className="font-semibold" style={{ margin: '16px 0 8px' }}>Eventos</div>
        <div className="flex flex-col gap-2">
          {data.events.map((ev) => (
            <Checkbox onCheckedChange={() => setHook({ ...hook, events: hook.events.includes(ev.id) ? hook.events.filter((x) => x !== ev.id) : [...hook.events, ev.id] })} key={ev.id} checked={hook.events.includes(ev.id)} label={<>
              {`${ev.label} (${ev.id})`}
            </>} />
          ))}
        </div>
        
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>API e webhooks</h2>
        <div style={{ color: 'var(--text-muted)' }}>
          Integre a plataforma com o seu sistema, n8n, Make, Zapier ou RD Station: use a API para cadastrar contatos, inscrever em sequências e enviar mensagens, e receba os eventos no seu endereço.
        </div>
      </div>

      <div className="page-content space-y-6">
        {/* Chaves */}
        <section className="brand-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h4 className="text-base font-semibold">Chaves de API</h4>
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>Cada chave dá acesso aos dados desta organização. Guarde em lugar seguro: ela só aparece na criação.</div>
            </div>
            <Button variant="primary" icon={ICONS.Plus} onClick={() => { setKeyName(''); setNewKeyOpen(true); }}>Nova chave</Button>
          </div>
          {activeKeys.length > 0 && (
            <ul className="account-list" style={{ marginTop: 12 }}>
              {activeKeys.map((k) => (
                <li key={k.id}>
                  <ICONS.Key size={16} />
                  <div>
                    <strong>{k.name}</strong>
                    <span><code>{k.prefix}…</code> · criada em {new Date(k.createdAt).toLocaleDateString('pt-BR')} · {k.lastUsedAt ? `último uso ${new Date(k.lastUsedAt).toLocaleString('pt-BR')}` : 'nunca usada'}</span>
                  </div>
                  <DangerButton variant="dangerOutline" onClick={() => setRevoking(k)}>Revogar</DangerButton>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Webhooks */}
        <section className="brand-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h4 className="text-base font-semibold">Webhooks</h4>
              <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>Enviamos um POST em JSON para o seu endereço (https) a cada evento, assinado no cabeçalho <code>X-Ingrave-Signature</code>. Falhas são tentadas de novo por até 6 horas.</div>
            </div>
            <Button variant="primary" icon={ICONS.Plus} onClick={() => { setHook({ url: '', events: data.events.map((e) => e.id) }); setHookOpen(true); }}>Novo webhook</Button>
          </div>
          {data.endpoints.length > 0 && (
            <ul className="account-list" style={{ marginTop: 12 }}>
              {data.endpoints.map((e) => (
                <li key={e.id}>
                  <ICONS.Connect size={16} />
                  <div>
                    <strong style={{ overflowWrap: 'anywhere' }}>{e.url}</strong>
                    <span>{e.events.map(label).join(' · ')}</span>
                    {e.lastDeliveryAt && (
                      <span style={{ color: e.lastError ? 'var(--crm-text-error)' : 'var(--brand-text)' }}>
                        Última entrega {new Date(e.lastDeliveryAt).toLocaleString('pt-BR')}: {e.lastError || `HTTP ${e.lastStatus}`}
                        {e.failureCount > 0 ? ` · ${e.failureCount} falha${e.failureCount !== 1 ? 's' : ''} seguida${e.failureCount !== 1 ? 's' : ''}` : ''}
                      </span>
                    )}
                    {!e.active && <span style={{ color: 'var(--crm-text-warning)' }}>Desligado{e.failureCount >= 50 ? ' automaticamente depois de muitas falhas' : ''}</span>}
                  </div>
                  <div className="api-row-actions">
                    <Switch onCheckedChange={async (checked: boolean) => { if (await call(`t-${e.id}`, `/api/integracoes/webhooks/${e.id}`, { method: 'PATCH', body: JSON.stringify({ active: checked }) })) load(); }} aria-label="Ativo" checked={e.active} />
                    <Button variant="secondary" loading={busy === `test-${e.id}`} onClick={async () => {
                      const r = await call(`test-${e.id}`, `/api/integracoes/webhooks/${e.id}/teste`, { method: 'POST' });
                      if (r) { r.success ? toast.success(`Teste entregue (HTTP ${r.responseStatus})`) : toast.error(`Teste falhou: ${r.error || r.responseStatus}`); load(); }
                    }}>Testar</Button>
                    <IconButtonRemove onClick={() => setRemoving(e)} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {data.deliveries.length > 0 && (
            <details className="api-deliveries">
              <summary>Entregas recentes ({data.deliveries.length})</summary>
              <table>
                <thead><tr><th>Quando</th><th>Evento</th><th>Situação</th><th>Tentativas</th><th>Resposta</th></tr></thead>
                <tbody>
                  {data.deliveries.map((d) => (
                    <tr key={d.id}>
                      <td>{new Date(d.createdAt).toLocaleString('pt-BR')}</td>
                      <td><code>{d.event}</code></td>
                      <td><Badge variant={BADGE_COLOR[(DELIVERY[d.status] ?? DELIVERY.PENDING).variant]}>{(DELIVERY[d.status] ?? DELIVERY.PENDING).label}</Badge></td>
                      <td>{d.attempts}</td>
                      <td>{d.error || (d.responseStatus ? `HTTP ${d.responseStatus}` : '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </section>

        {/* Como usar */}
        <section className="brand-card">
          <h4 className="text-base font-semibold">Como usar</h4>
          <div style={{ color: 'var(--text-muted)', marginTop: 2, marginBottom: 12 }}>Endereço base: <code>{base}</code> · limite de 120 chamadas por minuto por chave.</div>
          <div className="api-docs">
            <div>
              <strong>Endpoints</strong>
              <ul>
                <li><code>GET /me</code> organização da chave</li>
                <li><code>GET /connections</code> conexões e se podem enviar agora</li>
                <li><code>GET /contacts?phone=&amp;page=&amp;limit=</code> contatos</li>
                <li><code>POST /contacts</code> cria ou atualiza pelo telefone: <code>{'{ nome, telefone, email?, categoria?, observacoes? }'}</code></li>
                <li><code>POST /messages</code> texto avulso: <code>{'{ connection, to, text }'}</code></li>
              </ul>
            </div>
            <pre>{`curl -X POST ${base}/contacts \\
  -H "Authorization: Bearer ick_..." \\
  -H "Content-Type: application/json" \\
  -d '{"nome":"Maria","telefone":"11999998888","categoria":"Leads"}'`}</pre>
            <div>
              <strong>Conferir a assinatura do webhook</strong>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>O cabeçalho vem como <code>t=&lt;unix&gt;,v1=&lt;hex&gt;</code>; calcule o HMAC-SHA256 de <code>{'`${t}.${corpo}`'}</code> com o segredo do webhook e compare com <code>v1</code>.</div>
            </div>
            <pre>{`const [, t, v1] = header.match(/t=(\\d+),v1=([0-9a-f]+)/);
const esperado = crypto.createHmac('sha256', SEGREDO).update(\`\${t}.\${corpoBruto}\`).digest('hex');
const valido = crypto.timingSafeEqual(Buffer.from(v1), Buffer.from(esperado));`}</pre>
          </div>
        </section>
      </div>





      <Dialog.Root open={!!secret} onOpenChange={(o: boolean) => { if (!o) (() => setSecret(null))(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{secret ? <h3 className="text-lg font-semibold">{secret.title}</h3> : undefined}</Dialog.Title>

        {secret && (
          <>
            <div className="mb-4"><Banner variant="alert" description={<>{secret.hint}</>} /></div>
            <div className="flex gap-2 items-end">
              <div style={{ flex: 1 }}>
                <Field label="Valor">
                  <Input  value={secret.value} readOnly onFocus={(e) => e.currentTarget.select()} />
                </Field>
              </div>
              <Button variant="secondary" icon={ICONS.Copy} onClick={() => copy(secret.value)}>Copiar</Button>
            </div>
            <div className="flex justify-end" style={{ marginTop: 20 }}><Button variant="primary" onClick={() => setSecret(null)}>Já copiei</Button></div>
          </>
        )}
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={!!revoking} title="Revogar chave" confirmText="Revogar" objectName={revoking?.name} onCancel={() => setRevoking(null)}
        onConfirm={async () => { const k = revoking; setRevoking(null); if (k && await call(`r-${k.id}`, `/api/integracoes/chaves/${k.id}`, { method: 'DELETE' }, 'Chave revogada')) load(); }}>
        {revoking ? `A chave "${revoking.name}" para de funcionar na hora. Integrações que a usam deixam de acessar a API.` : ''}
      </DangerConfirmModal>
      <DangerConfirmModal open={!!removing} title="Excluir webhook" confirmText="Excluir" objectName={removing?.url} onCancel={() => setRemoving(null)}
        onConfirm={async () => { const e = removing; setRemoving(null); if (e && await call(`d-${e.id}`, `/api/integracoes/webhooks/${e.id}`, { method: 'DELETE' }, 'Webhook excluído')) load(); }}>
        {removing ? `${removing.url} deixa de receber eventos.` : ''}
      </DangerConfirmModal>
    </>
  );
}

function IconButtonRemove({ onClick }: { onClick: () => void }) {
  return <DangerButton variant="dangerOutline" aria-label="Excluir webhook" icon={ICONS.Trash} onClick={onClick} />;
}
